import { ChangeEvent, useState, useEffect, useRef, KeyboardEvent } from "react";
import { BiSearch } from "react-icons/bi";
import { Flex, Box, Text } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";
import { useRecoilState } from "recoil";

import { NoResults } from "../NoResults";
import { DropdownItem } from "../DropdownItem";
import { useUserSuggestionsForSearch } from "MVVM/Hooks/useSuggestedUsers";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";

import StackedAvatars from "../StackedAvatars";
import { AllstarSearchSuggestionsPreview } from "MVVM/State";

export interface ISearchItem {
  id: string;
  name: string;
  image: string;
  gameId?: string;
  reason?: string;
  userGroup?: {
    type: string;
  };
  rank?: number;
  steam?: {
    steamid?: string;
    personaname?: string;
  };
  isProPlayer?: boolean;
  game?: string;
}

const NavSearchBar = ({
  items,
  loading,
  onSelect,
  onFocus,
  debouncedSearch,
  placeholder,
}: {
  items: ISearchItem[];
  loading: boolean;
  onSelect: (item: ISearchItem) => void;
  onFocus: () => void;
  debouncedSearch: (query: string) => void;
  placeholder: string;
}) => {
  const { suggestedUsers } = useUserSuggestionsForSearch();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isServerSearching = inputValue.trim().length >= 3;
  const query = inputValue.trim().toLowerCase();

  const [searchSuggestionsPreview, setSearchSuggestionsPreview] =
    useRecoilState(AllstarSearchSuggestionsPreview);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setInputValue("");
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debouncedSearchTimeoutRef.current) {
        clearTimeout(debouncedSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = (item: ISearchItem) => {
    onSelect(item);
    setIsOpen(false);
    setShowMobile(false);
    setInputValue("");
    setSelectedIndex(-1);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setInputValue(value);
    setIsOpen(true);
    setSelectedIndex(-1);

    if (value.trim().length >= 3) {
      debouncedSearch(value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const searchResults = getSearchResults();

    if (!isOpen || !searchResults?.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev < searchResults.length - 1 ? prev + 1 : 0;
          scrollToSelectedItem(newIndex);
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : searchResults.length - 1;
          scrollToSelectedItem(newIndex);
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleSelect(searchResults[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    onFocus();
    setIsOpen(true);
    setSelectedIndex(-1);
    inputRef.current && inputRef.current.focus();

    setSearchSuggestionsPreview({
      seenUserIds: previewUsers().map((u) => u.id),
    });
  };

  const setMobileFocus = () => {
    setShowMobile(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const getSearchResults = (): ISearchItem[] => {
    if (!query) {
      return suggestedUsers;
    }

    if (!isServerSearching) {
      return suggestedUsers.filter((user) =>
        user.name.toLowerCase().includes(query),
      );
    }

    return items;
  };

  const scrollToSelectedItem = (index: number) => {
    const dropdownItems = dropdownRef.current?.querySelectorAll(
      "[data-dropdown-item]",
    );
    if (dropdownItems && dropdownItems[index]) {
      dropdownItems[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  // These are just the first X users with an avatar
  const previewUsers = () => {
    return suggestedUsers.filter((user) => user.image).slice(0, 3);
  };

  // These are the preview users we have not seen yet
  const unseenPreviewUsers = () => {
    const seenUserIds = searchSuggestionsPreview?.seenUserIds;
    return previewUsers().filter((p) => !seenUserIds?.includes(p.id));
  };

  return (
    <>
      <MobileStyledSearch onClick={setMobileFocus} />
      <Container ref={wrapperRef} showMobile={showMobile}>
        <Flex alignItems="center" style={{ gap: 16 }}>
          <InputWrapper>
            <Flex alignItems="center" style={{ flex: 1, minWidth: 0 }}>
              <StyledSearch />
              <StyledInput
                onChange={handleInputChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || "Search..."}
                ref={inputRef}
                type="text"
                value={inputValue}
              />
            </Flex>
            {!isOpen && (
              <AnimatedTooltip
                content="You can now look up professional players for Counter-Strike and League of Legends!"
                position="bottom"
                maxWidth={200}
              >
                <NewBadge>NEW</NewBadge>
              </AnimatedTooltip>
            )}
            {
              <StackedAvatars
                users={unseenPreviewUsers()}
                visible={!isOpen}
                onClick={handleFocus}
              />
            }
          </InputWrapper>
          <Text
            color="envy"
            display={["block", "block", "none"]}
            fontWeight={500}
            mr={2}
            onClick={() => setShowMobile(false)}
            style={{ zIndex: "1000" }}
          >
            Cancel
          </Text>
        </Flex>
        <SearchContainer isOpen={isOpen}>
          <InputPlaceholder />
          <DropdownWrapper isVisible={isOpen} ref={dropdownRef}>
            {(() => {
              const searchResults = getSearchResults();

              return searchResults?.length ? (
                searchResults.map((item: ISearchItem, idx: number) => (
                  <DropdownItem
                    key={item.id}
                    avatar={item.image}
                    username={item.name}
                    callback={() => handleSelect(item)}
                    reason={item.reason}
                    userGroup={item.userGroup}
                    rank={item.rank}
                    steam={item.steam}
                    isSelected={idx === selectedIndex}
                    isProPlayer={item.isProPlayer}
                    game={item.game}
                  />
                ))
              ) : (
                <NoResults loading={isServerSearching && loading} />
              );
            })()}
          </DropdownWrapper>
        </SearchContainer>
      </Container>
    </>
  );
};

const Container = styled(Box)<{ showMobile: boolean }>`
  cursor: pointer !important;
  position: relative;
  width: 380px;
  z-index: 5;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    width: 300px;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    background-color: ${({ theme }) => theme.colors.ned};
    height: 100%;
    left: 0;
    padding: 20px 16px;
    position: absolute;
    top: 0;
    width: 100%;

    display: ${(props) => (props.showMobile ? "block" : "none")};
  }
`;

const InputPlaceholder = styled(Box)`
  height: 40px;
  margin-bottom: 10px;
`;

const SearchContainer = styled(Box)<{ isOpen: boolean }>`
  background-color: ${({ theme, isOpen }) =>
    isOpen ? theme.colors.ned : "transparent"};
  border-radius: 20px;
  box-shadow: ${({ isOpen }) =>
    isOpen ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "none"};
  left: 0;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  padding: ${({ isOpen }) => (isOpen ? "10px" : "0")};
  position: absolute;
  top: -12px;
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  width: 100%;
  z-index: 999;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    box-shadow: none;
    opacity: 1;
    top: 12px;
    visibility: visible;
    width: 100vw;
  }
`;

const InputWrapper = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.midnightAlpha50};
  border-radius: 20px;
  border: 2px solid transparent;
  height: 40px;
  justify-content: left;
  margin: 0 auto;
  padding: 3px;
  position: relative;
  width: 96%;
  z-index: 1000;

  &:hover {
    cursor: text;
    background-color: ${({ theme }) => theme.colors.gray850};
  }

  &:focus-within {
    border: 2px solid ${({ theme }) => theme.colors.envy};
  }
`;

const StyledSearch = styled(BiSearch)`
  border-radius: 20px;
  fill: ${({ theme }) => theme.colors.chalk};
  height: 24px;
  padding: 0 0 0 8px;
  width: 24px;
`;

const MobileStyledSearch = styled(BiSearch)`
  display: none;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: block;
    fill: ${({ theme }) => theme.colors.chalk};
    height: 24px;
    margin-left: auto;
    margin-right: 18px;
    padding: 0 0 0 8px;
    width: 24px;
  }
`;

const StyledInput = styled.input`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 500;
  height: 100%;
  outline: none;
  padding-left: 12px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
  }
`;

const DropdownWrapper = styled(Box)<{ isVisible: boolean }>`
  background-color: ${({ theme }) => theme.colors.ned};
  border-radius: 12px;
  display: ${({ isVisible }) => (isVisible ? "block" : "none")};
  height: ${({ isVisible }) => (isVisible ? "auto" : 0)};
  max-height: 250px;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  overflow-y: auto;
  padding-right: 8px;
  transition: opacity 0.2s;
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    display: none;
    position: absolute;
    right: 0px;
    top: 0px;
    width: 12px;
    height: 100%;
    z-index: 999;
    background: #192832;
    border-left: 1px solid #20323f;
  }

  ::-webkit-scrollbar-thumb {
    position: relative;
    display: block;
    height: 20px;
    width: 10px;
    border-radius: 4px;
    margin-left: 2px;
    background-color: #253a49;
    cursor: pointer;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    max-height: 100vh;
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(80, 227, 194, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(80, 227, 194, 0);
  }
`;

const NewBadge = styled(Text)`
  animation: ${pulse} 2s ease-in-out infinite;
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  flex-shrink: 0;
  font-family: Barlow;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1;
  margin-right: 4px;
  padding: 4px 7px;
  text-transform: uppercase;
  white-space: nowrap;
`;

export { NavSearchBar };
